import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  getMyApprovalForPeriod,
  submitMyPayPeriodApproval,
  type PayPeriodApproval,
} from "@/lib/api";
import { formatHours } from "@/lib/hoursMath";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Matches backend EmployeeSubmitBody.employee_note max_length. */
export const EMPLOYEE_NOTE_MAX_CHARS = 2000;

const wrapTextClass =
  "whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] max-w-full min-w-0";

/**
 * Employee (or any logged-in user) approve / dispute the selected pay period.
 */
export function PayPeriodApprovalPanel({
  periodFrom,
  periodTo,
  payWeek,
  regularHours,
  otHours,
}: {
  periodFrom: string;
  periodTo: string;
  payWeek?: string;
  regularHours: number;
  otHours: number;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["ppa-me", periodFrom, periodTo],
    queryFn: () => getMyApprovalForPeriod(periodFrom, periodTo),
  });

  const mut = useMutation({
    mutationFn: (status: "approved" | "disputed") =>
      submitMyPayPeriodApproval({
        period_from: periodFrom,
        period_to: periodTo,
        pay_week: payWeek,
        employee_status: status,
        employee_note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setMsg("Saved.");
      setNote("");
      void qc.invalidateQueries({ queryKey: ["ppa-me", periodFrom, periodTo] });
      void qc.invalidateQueries({ queryKey: ["ppa-team"] });
    },
    onError: (e: Error) => setMsg(e.message || "Submit failed"),
  });

  const a: PayPeriodApproval | null | undefined = q.data?.approval;
  const noteLen = note.length;
  const nearLimit = noteLen >= EMPLOYEE_NOTE_MAX_CHARS * 0.9;

  const empBadge = () => {
    if (!a) return <Badge variant="outline">Not submitted</Badge>;
    if (a.employee_status === "approved") return <Badge variant="secondary">You approved</Badge>;
    if (a.employee_status === "disputed") return <Badge variant="destructive">You disputed</Badge>;
    return <Badge variant="outline">{a.employee_status}</Badge>;
  };

  const mgrBadge = () => {
    if (!a) return null;
    if (a.manager_status === "approved") return <Badge variant="secondary">Manager approved</Badge>;
    if (a.manager_status === "rejected") return <Badge variant="destructive">Manager rejected</Badge>;
    if (a.employee_status !== "pending") return <Badge variant="outline">Awaiting manager</Badge>;
    return null;
  };

  return (
    <div className="rounded border border-border p-3 space-y-2 min-w-0 max-w-full w-full overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium min-w-0">
        <span>Pay period approval</span>
        {empBadge()}
        {mgrBadge()}
      </div>
      <p className={`text-xs text-muted-foreground ${wrapTextClass}`}>
        Confirm hours for week {payWeek || "?"} ({periodFrom} → {periodTo}). Shown totals: Regular{" "}
        {formatHours(regularHours)} · OT1 {formatHours(otHours)}. Disputes require a note
        (use Field Notes for punch corrections).
      </p>
      {a?.employee_note && (
        <div className={`text-xs text-muted-foreground ${wrapTextClass}`}>
          <span className="font-medium text-foreground/80">Your note: </span>
          {a.employee_note}
        </div>
      )}
      {a?.manager_note && (
        <div className={`text-xs text-muted-foreground ${wrapTextClass}`}>
          <span className="font-medium text-foreground/80">Manager note: </span>
          {a.manager_note}
        </div>
      )}
      <div className="grid gap-1 w-full min-w-0 max-w-full">
        <div className="flex items-baseline justify-between gap-2 min-w-0">
          <Label className="text-xs text-muted-foreground min-w-0" htmlFor="ppa-employee-note">
            Note (required for dispute; optional on approve)
          </Label>
          <span
            className={`text-[10px] tabular-nums shrink-0 ${
              nearLimit ? "text-destructive font-medium" : "text-muted-foreground"
            }`}
          >
            {noteLen}/{EMPLOYEE_NOTE_MAX_CHARS}
          </span>
        </div>
        <Textarea
          id="ppa-employee-note"
          className="min-h-[88px] w-full max-w-full min-w-0 text-sm resize-y break-words [overflow-wrap:anywhere]"
          value={note}
          maxLength={EMPLOYEE_NOTE_MAX_CHARS}
          rows={4}
          onChange={(e) => setNote(e.target.value.slice(0, EMPLOYEE_NOTE_MAX_CHARS))}
          placeholder="Describe the dispute (required), or add an optional note when approving"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={mut.isPending}
          onClick={() => {
            setMsg(null);
            mut.mutate("approved");
          }}
        >
          Approve period
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={mut.isPending}
          onClick={() => {
            setMsg(null);
            if (!note.trim()) {
              setMsg("Add a note when disputing.");
              return;
            }
            mut.mutate("disputed");
          }}
        >
          Dispute period
        </Button>
      </div>
      {msg && <div className={`text-xs text-muted-foreground ${wrapTextClass}`}>{msg}</div>}
    </div>
  );
}
